import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatLauncher() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey! I'm Rosh. Ask me anything about my work." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    if (!text.trim() || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setLoading(true);

    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 60000); // 60s ceiling
    try {
      const res = await fetch(`${API}/api/chat/portfolio`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${t ? ` — ${t.slice(0, 120)}` : ""}`);
      }
      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: data.response || "Sorry, something went wrong on my end. Try again in a moment.",
        },
      ]);
    } catch (err) {
      clearTimeout(timeoutId);
      const isAbort = err && (err.name === "AbortError" || String(err).includes("aborted"));
      const label = isAbort
        ? "That one took too long — the AI service didn't respond in time. Give it another go, or ping me directly:"
        : "I'm having trouble reaching my brain right now. Try again in a moment, or ping me directly:";
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            `${label}\n\nhi@rosh.work\n` +
            `[LinkedIn](https://www.linkedin.com/in/roshanraykar)\n` +
            `[WhatsApp me](https://wa.me/917204836788?text=Hi%2C%20I%20have%20checked%20your%20portfolio%20on%20rosh.work%20and%20would%20like%20to%20discuss%20further.)`,
        },
      ]);
    }
    setLoading(false);
  };

  const renderText = (text) =>
    text
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(
        /\[([^\]]+)\]\(((?:https?:\/\/|mailto:|tel:|#|\/)[^)]+)\)/g,
        (_m, label, href) => {
          const isHash = href.startsWith("#") || href.startsWith("/#");
          const target = isHash ? "_self" : "_blank";
          const rel = isHash ? "" : ' rel="noopener"';
          return `<a href="${href}" target="${target}"${rel} class="underline font-medium" style="color:#B5793F">${label}</a>`;
        }
      )
      .replace(/\n/g, "<br>");

  return (
    <>
      {/* LAUNCHER — bottom-right, on-brand cream/ink */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -2 }}
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher-btn"
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[60] flex items-center gap-2.5 pl-4 pr-5 py-3 rounded-full"
        style={{
          background: "#262016",
          color: "#EDE7D9",
          boxShadow: "0 12px 32px -8px rgba(38,32,22,0.45), 0 4px 12px rgba(38,32,22,0.18)",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "#B5793F" }}>
          {open ? <X size={14} strokeWidth={2.2} /> : <MessageCircle size={14} strokeWidth={2.2} />}
        </span>
        <span className="text-sm font-medium tracking-wide">{open ? "Close" : "Ask Rosh"}</span>
      </motion.button>

      {/* PANEL */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[59] flex flex-col"
            style={{
              right: "1rem",
              bottom: "5rem",
              width: "min(96vw, 400px)",
              height: "min(70vh, 600px)",
              background: "#F6F1E5",
              borderRadius: "22px 18px 24px 20px",
              boxShadow: "0 24px 60px -16px rgba(38,32,22,0.35), 0 6px 18px rgba(38,32,22,0.12)",
              border: "1px solid rgba(38,32,22,0.08)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-[#26201610] flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg, #B5793F, #19B5DE)" }}>
                <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>R</span>
              </div>
              <div className="flex-1">
                <p className="rp-display text-base font-medium text-[#262016] leading-tight">Rosh</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn"
                      className="text-[#8A7E6B] hover:text-[#262016] transition-colors" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className="max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
                    style={
                      m.role === "user"
                        ? { background: "#262016", color: "#EDE7D9", borderBottomRightRadius: 4 }
                        : { background: "#FFFFFF", color: "#262016", border: "1px solid rgba(38,32,22,0.06)", borderBottomLeftRadius: 4 }
                    }
                    dangerouslySetInnerHTML={{ __html: renderText(m.text) }}
                  />
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#26201610] rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="w-1.5 h-1.5 bg-[#B5793F] rounded-full animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-[#26201610] flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send(input)}
                placeholder="Ask me anything..."
                data-testid="chat-input"
                className="flex-1 bg-white border border-[#26201618] rounded-full px-4 py-2.5 text-sm text-[#262016] focus:outline-none focus:border-[#B5793F] transition-colors"
              />
              <button
                onClick={() => send(input)}
                disabled={!input.trim() || loading}
                data-testid="chat-send-btn"
                className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30 transition-all shrink-0"
                style={{ background: "#262016", color: "#EDE7D9" }}
              >
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
